import { useState } from "react";
import { toast } from "sonner";
import { useLeaveCompetitionMutation } from "@/features/predictions/hooks/useLeaveCompetitionMutation";
import { storageService } from "@/shared/services/localStorage.service";

export const PRONOSTICS_ONBOARDING_KEY = "pronostics_onboarding_completed";
export const PRONOSTICS_ONBOARDING_TOTAL_STEPS = 4;

export type PronosticsOnboardingStep = 1 | 2 | 3 | 4;

const clampStep = (value: number): PronosticsOnboardingStep => {
  if (value <= 1) return 1;
  if (value >= PRONOSTICS_ONBOARDING_TOTAL_STEPS) return 4;
  return value as PronosticsOnboardingStep;
};

const readCompletedFromStorage = (): boolean => {
  try {
    return storageService.getItem(PRONOSTICS_ONBOARDING_KEY) === "true";
  }
  catch {
    return false;
  }
};

const persistCompletion = (): void => {
  try {
    storageService.setItem(PRONOSTICS_ONBOARDING_KEY, "true");
  }
  catch {
    // ignore storage errors, still close the wizard
  }
};

export const usePronosticsOnboarding = () => {
  const [shouldShow, setShouldShow] = useState<boolean>(() => !readCompletedFromStorage());
  const [currentStep, setCurrentStep] = useState<PronosticsOnboardingStep>(1);
  const [optOut, setOptOut] = useState<boolean>(false);
  const leaveMutation = useLeaveCompetitionMutation();

  const next = () => setCurrentStep((step) => clampStep(step + 1));
  const prev = () => setCurrentStep((step) => clampStep(step - 1));

  const close = () => {
    setShouldShow(false);
  };

  const complete = async (): Promise<void> => {
    if (optOut) {
      try {
        await leaveMutation.mutateAsync();
        toast.success("Tu ne participeras pas à la compétition.");
      }
      catch {
        toast.error("Impossible d'enregistrer ton choix. Réessaye.");
        return;
      }
    }
    persistCompletion();
    setShouldShow(false);
  };

  return {
    shouldShow,
    currentStep,
    totalSteps: PRONOSTICS_ONBOARDING_TOTAL_STEPS,
    next,
    prev,
    close,
    complete,
    optOut,
    setOptOut,
    isLeaving: leaveMutation.isPending,
  };
};
